import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Callout, Icon, LoadingState, ScrollBar, toastSuccess } from 'admination-react'
import { FormRenderer } from '@/core/components/form/FormRenderer'
import { updateFormField, type FormValues } from '@/core/components/form/formModel'
import { dokumentationSpeichern } from '@/core/data/aufgaben'
import { getEintrag } from '@/core/data/dokumente'
import { todayIso } from '@/core/lib/dates'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { useApp } from '@/core/state/appStore'
import { aufgabeDokumentationForm } from '@/core/templates'
import { ModalHeader } from '../ModalHeader'

const werteVon = (bearbeitetVon: string, datum: string, dokumentation: string): FormValues => ({ aufgabe: { bearbeitetVon, datum, dokumentation } })

export function AufgabeFormular() {
  const { t } = useTranslation('vergabeStaff')
  const { state, dispatch, identity, reloadDashboard } = useApp()
  const scrollerRef = useRef<HTMLElement>(null)
  const aufgabeId = state.screen?.aufgabeId
  const dokumentId = state.screen?.document.id ?? null
  const [werte, setWerte] = useState<FormValues | null>(dokumentId ? null : werteVon(`${identity?.vorname ?? ''} ${identity?.nachname ?? ''}`.trim(), todayIso(), ''))
  const [ladeFehler, setLadeFehler] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const datum = String(werte?.aufgabe?.datum ?? '')
  const text = String(werte?.aufgabe?.dokumentation ?? '')

  useEffect(() => {
    if (!dokumentId) return
    let cancelled = false
    getEintrag(dokumentId).then(
      (eintrag) => { if (!cancelled) setWerte(werteVon(eintrag.bearbeitetVon, eintrag.datum, eintrag.text)) },
      (error: unknown) => {
        console.error('Eintrag laden fehlgeschlagen:', error)
        if (!cancelled) setLadeFehler(friendlyErrorMessage(error, t('aufgabe.ladeFehler')))
      },
    )
    return () => { cancelled = true }
  }, [dokumentId, t])

  async function speichern() {
    if (!aufgabeId) return
    setBusy(true)
    setFehler(null)
    try {
      const { drin } = await dokumentationSpeichern(aufgabeId, dokumentId === null, { datum, text })
      toastSuccess(t('aufgabe.gespeichert'))
      reloadDashboard()
      dispatch({ type: 'GO_BACK' })
      if (drin === false) dispatch({ type: 'CHANGE_TAB', tab: 'uebersicht' })
    } catch (error) {
      console.error('Dokumentation speichern fehlgeschlagen:', error)
      setFehler(friendlyErrorMessage(error, t('aufgabe.speichernFehler')).replace(/^(?:datum|text): /, ''))
    } finally {
      setBusy(false)
    }
  }

  return <>
    <ModalHeader closeDisabled={busy}>
      <div className="pill-cluster">
        <div className="pill">
          <Button primaryAction data-testid="aufgabe-speichern" disabled={!werte || !datum || !text.trim() || busy} onClick={() => void speichern()}>
            <Icon name="i-check" />
            {t('aufgabe.speichern')}
          </Button>
        </div>
      </div>
    </ModalHeader>
    <main className="form" data-scroll-host ref={scrollerRef}>
      {werte
        ? <div className="form-column">
          <FormRenderer
            definition={aufgabeDokumentationForm}
            values={werte}
            onChange={(id, wert) => setWerte((alt) => updateFormField(alt, id, wert))}
            section={() => ({ heading: state.screen?.document.name ?? '' })}
            field={(feld) => ({ label: t(feld.id), ...(feld.control === 'date' && { maxIso: todayIso() }) })}
          />
          {fehler && <Callout tone="error" role="alert" data-testid="aufgabe-fehler">{fehler}</Callout>}
        </div>
        : ladeFehler
          ? <Callout tone="error" role="alert">{ladeFehler}</Callout>
          : <LoadingState>{t('aufgabe.laden')}</LoadingState>}
    </main>
    <ScrollBar scrollerRef={scrollerRef} />
  </>
}
